// Importar estilos
import '@styles/main.css';
import '@styles/ar-components.css';
import '@styles/ui-elements.css';

// Importar componentes
import { StateManager } from '@components/StateManager';
import { HitboxSystem } from '@components/HitboxSystem';
import { UIManager } from '@components/UIManager';
import { ARScene } from '@components/ARScene';

// Importar utilidades
import { onDOMReady } from '@utils/domUtils';

// Importar tipos
import type {
  ARHiroApp as IARHiroApp,
  StateChangeCallback
} from '@types';

// Registrar componente para links
AFRAME.registerComponent('url-link', {
  init: function () {
    this.el.addEventListener('click', (event: Event) => {
      // Verificar si el elemento y sus padres están visibles
      if (!this.isElementAndParentsVisible(this.el)) {
        console.log('Elemento no visible, ignorando clic en URL');
        return;
      }

      const url = this.el.getAttribute('data-link');
      if (url) {
        // Prevenir propagación del evento
        event.stopPropagation();
        event.preventDefault();

        // Abrir en nueva pestaña
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    });
  },

  // Función para verificar visibilidad del elemento y sus padres
  isElementAndParentsVisible: function (element: any) {
    let currentElement = element;

    while (currentElement) {
      // Verificar visibilidad del objeto3D
      if (!currentElement.object3D || !currentElement.object3D.visible) {
        return false;
      }

      // Verificar atributo visible de A-Frame
      const visibleAttr = currentElement.getAttribute('visible');
      if (visibleAttr === 'false' || visibleAttr === false) {
        return false;
      }

      // Subir al elemento padre
      currentElement = currentElement.parentElement;

      // Parar en el marcador Hiro o en la escena
      if (currentElement && (currentElement.tagName === 'A-MARKER' || currentElement.tagName === 'A-SCENE')) {
        break;
      }
    }

    return true;
  }
});

// Función que detecta si es un dispositivo iOS
function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

// Función que detecta si es un navegador Safari o un dispositivo iOS
function isSafariOrIOS(): boolean {
  const ua = navigator.userAgent;
  const isIOSDevice = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isSafariEngine = /Safari/i.test(ua) && !/Chrome|CriOS|Edg|OPR|Firefox/i.test(ua);
  return isIOSDevice || isSafariEngine;
}

/**
 * Clase principal de la aplicación
 */
class ARHiroApp implements IARHiroApp {
  public components: {
    stateManager?: StateManager;
    hitboxSystem?: HitboxSystem;
    uiManager?: UIManager;
    arScene?: ARScene;
  } = {};

  public isInitialized: boolean = false;

  constructor() {
    console.log(' Iniciando aplicación AR Hiro...');
  }

  /**
   * Inicializa la aplicación
   */
  async initialize(): Promise<void> {
    try {
      console.log(' Inicializando componentes...');

      // 1. Inicializar gestor de estados
      this.components.stateManager = new StateManager();
      console.log('StateManager inicializado');

      // 2. Inicializar sistema de hitboxes
      this.components.hitboxSystem = new HitboxSystem(this.components.stateManager);
      console.log('HitboxSystem inicializado');

      // 3. Inicializar gestor de UI
      this.components.uiManager = new UIManager(this.components.hitboxSystem);
      console.log('UIManager inicializado');

      // 4. Inicializar escena AR
      this.components.arScene = new ARScene(
        this.components.stateManager,
        this.components.hitboxSystem
      );
      console.log('ARScene inicializado');

      // 5. Configurar comunicación entre componentes
      this.setupComponentCommunication();

      // 6. Configurar manejo de errores global
      this.setupErrorHandling();

      this.isInitialized = true;
      console.log('Aplicación AR inicializada correctamente');

      // 7. Mostrar información de debug
      this.logSystemInfo();

    } catch (error) {
      console.error('Error inicializando aplicación:', error);
      this.handleInitializationError(error as Error);
    }
  }

  /**
   * Configura comunicación entre componentes
   */
  private setupComponentCommunication(): void {
    if (!this.components.stateManager) return;

    // Configurar callbacks de cambio de estado
    const stateChangeCallback: StateChangeCallback = (from: string, to: string) => {
      console.log(`Estado cambiado: ${from} → ${to}`);

      // Notificar a otros componentes si es necesario
      this.onStateChange(from, to);
    };

    this.components.stateManager.onStateChange(stateChangeCallback);

    console.log('Comunicación entre componentes configurada');
  }

  /**
   *  Configura manejo de errores global
   */
  private setupErrorHandling(): void {
    // Capturar errores no manejados
    window.addEventListener('error', (event: ErrorEvent) => {
      console.error(' Error global capturado:', event.error);
      this.handleGlobalError(event.error);
    });

    // Capturar promesas rechazadas
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      console.error(' Promesa rechazada:', event.reason);
      this.handleGlobalError(event.reason);
    });

    console.log(' Manejo de errores global configurado');
  }

  /**
   * Maneja cambios de estado
   */
  private onStateChange(_from: string, _to: string): void {
    // Aquí puedes agregar lógica adicional para cambios de estado
    // Por ejemplo, analytics, logging, etc.

    // Actualizar hitboxes si están visibles
    if (this.components.hitboxSystem?.showCalibration) {
      this.components.hitboxSystem.updateCalibrationDisplay();
    }
  }

  /**
   * Maneja errores de inicialización
   */
  private handleInitializationError(error: Error): void {
    const errorMessage = `
      <div style="
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: rgba(255, 0, 0, 0.9);
        color: white;
        padding: 20px;
        border-radius: 10px;
        z-index: 10000;
        text-align: center;
        max-width: 80%;
      ">
        <h3>Error de Inicialización</h3>
        <p>${error.message}</p>
        <p>Por favor, recarga la página.</p>
        <button onclick="window.location.reload()" style="
          background: white;
          color: red;
          border: none;
          padding: 10px 20px;
          border-radius: 5px;
          cursor: pointer;
          margin-top: 10px;
        ">Recargar</button>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', errorMessage);
  }

  /**
   *  Maneja errores globales
   */
  private handleGlobalError(error: Error): void {
    // Log del error
    console.error('Error global:', error);

    // Solo mostrar errores críticos al usuario, no errores menores como problemas de video
    const errorMessage = error.message?.toLowerCase() || '';
    const isCriticalError = !errorMessage.includes('video') &&
      !errorMessage.includes('play') &&
      !errorMessage.includes('autoplay') &&
      !errorMessage.includes('media');

    // Mostrar notificación al usuario solo para errores críticos
    if (this.components.uiManager && isCriticalError) {
      this.components.uiManager.showError(
        'Ha ocurrido un error. La aplicación puede no funcionar correctamente.',
        5000
      );
    }
  }

  /**
   * Muestra información del sistema
   */
  private logSystemInfo(): void {
    const info = {
      app: 'AR Hiro Modular',
      version: '1.0.0',
      typescript: true,
      components: Object.keys(this.components),
      browser: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      timestamp: new Date().toISOString()
    };

    console.log('Información del sistema:', info);

    // Verificar compatibilidad
    if (this.components.arScene) {
      const compatibility = this.components.arScene.checkBrowserCompatibility();
      console.log('Compatibilidad:', compatibility);
    }
  }

  /**
   * Obtiene información del estado de la aplicación
   */
  getAppState(): any {
    if (!this.isInitialized) {
      return { initialized: false };
    }

    return {
      initialized: this.isInitialized,
      state: this.components.stateManager?.getStateInfo(),
      scene: this.components.arScene?.getSceneInfo(),
      ui: this.components.uiManager?.getUIState(),
      hitboxes: {
        calibrationActive: this.components.hitboxSystem?.showCalibration || false
      }
    };
  }

  /**
   * Reinicia la aplicación
   */
  reset(): void {
    console.log('Reiniciando aplicación...');

    if (this.components.arScene) {
      this.components.arScene.reset();
    }

    if (this.components.stateManager) {
      this.components.stateManager.reset();
    }

    console.log('Aplicación reiniciada');
  }

  /**
   * Limpia recursos de la aplicación
   */
  cleanup(): void {
    console.log('Limpiando recursos...');

    Object.values(this.components).forEach(component => {
      if (component && typeof component.cleanup === 'function') {
        component.cleanup();
      }
    });

    this.isInitialized = false;
    console.log('Recursos limpiados');
  }
}

/**
 * Función de inicialización global
 */
function initializeApp(): void {
  // Crear instancia global de la aplicación
  window.arHiroApp = new ARHiroApp();

  // Inicializar cuando el DOM esté listo
  onDOMReady(async () => {
    await window.arHiroApp.initialize();
  });
}

// Función global para toggle de calibración (para el botón HTML)
window.toggleCalibration = function (): void {
  if (window.arHiroApp?.components?.uiManager) {
    window.arHiroApp.components.uiManager.toggleCalibration();
  } else {
    console.warn('UIManager no disponible para toggleCalibration');
  }
};

// Arreglar estilos en iOS
window.addEventListener('DOMContentLoaded', () => {
  const mainContent = document.getElementById('mainContent');

  if (mainContent) {
    const scale = isIOS() ? '1.5 0.5 1' : '1 1 1';
    mainContent.setAttribute('scale', scale);
  }
});

/**
 * Función que añade el source a los vídeos que están en assets usando detección de soporte real.
 * No fuerza codecs específicos y asegura load() antes de play().
 */
function addSourceToVideos() {
  // Selección condicionada por motor/navegador: Safari/iOS -> MP4; otros -> WebM primero
  const candidates = isSafariOrIOS()
    ? [
        { ext: 'mp4', type: 'video/mp4' },
        { ext: 'webm', type: 'video/webm' }
      ]
    : [
        { ext: 'webm', type: 'video/webm' },
        { ext: 'mp4', type: 'video/mp4' }
      ];

  const videos = [
    { id: 'videoInitial', files: ['Intro'] },
    { id: 'videoAuthentication', files: ['Authentication'] },
    { id: 'videoIDV', files: ['IDV'] },
    { id: 'videoOnboarding', files: ['Onboarding'] },
  ];

  videos.forEach(({ id, files }) => {
    const video = document.getElementById(id) as HTMLVideoElement | null;
    if (!video) return;

    // Limpiar fuentes previas si las hubiera
    video.innerHTML = '';

    let anySource = false;
    candidates.forEach(({ ext, type }) => {
      // canPlayType retorna "", "maybe" o "probably"; cualquier valor truthy indica soporte
      if (video.canPlayType(type)) {
        files.forEach((file) => {
          const source = document.createElement('source');
          source.src = `assets/videos/${file}.${ext}`;
          source.type = type;
          video.appendChild(source);
          anySource = true;
        });
      }
    });

    // Asegurar atributos para autoplay en móviles
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.muted = true;
    video.loop = true;

    if (anySource) {
      video.load();
      // No autoiniciar el video inicial hasta que se detecte el marcador
      if (id !== 'videoInitial') {
        video.autoplay = true;
        // Intento inicial de reproducción; si falla por política de autoplay, se reintenta tras gesto de usuario
        video.play().catch(() => {});
      } else {
        video.autoplay = false;
        // Aseguramos que empiece desde el principio cuando se dispare markerFound
        video.currentTime = 0;
        video.pause();
      }
    }
  });
}

// Reintento de reproducción tras el primer gesto del usuario (cumple políticas de autoplay)
function ensurePlaybackOnUserGesture() {
  const resume = () => {
    ['videoInitial','videoAuthentication','videoIDV','videoOnboarding'].forEach(id => {
      const v = document.getElementById(id) as HTMLVideoElement | null;
      if (v) v.play().catch(() => {});
    });
    window.removeEventListener('touchend', resume);
    window.removeEventListener('click', resume);
  };
  window.addEventListener('touchend', resume, { once: true });
  window.addEventListener('click', resume, { once: true });
}

addSourceToVideos();
ensurePlaybackOnUserGesture();

// Inicializar aplicación
initializeApp();

// Exportar para uso en módulos
export default ARHiroApp;